import * as React from 'react'

import { useQuery } from '@apollo/client'
import { Button } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { CheckFeatureAvailabilitiesDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { FACTORIAL_APP, PROFESSIONALS_APP } from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import useCreateManualIncidence from 'modules/payroll_bundle/hooks/useCreateManualIncidence'
import ManualIncidenceForm from 'modules/payroll_bundle/pages/IncidencesPage/components/_ManualIncidenceModal/_Form'
import Form from 'modules/payroll_bundle/pages/IncidencesPage/utils/manualIncidenceForm'

interface Props {
  onClose: () => void
  onSuccess?: () => void
}

const New = ({ onClose, onSuccess }: Props) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const isBookkeeperView = useBookkeeperView()

  const { data, loading: queryLoading } = useQuery(CheckFeatureAvailabilitiesDocument, {
    variables: {
      ids: [Features.compensations_management],
      companyId,
    },
    fetchPolicy: 'cache-first',
    skip: !companyId,
  })

  const hasCompensationsModule =
    data?.companies?.featureAvailabilitiesConnection?.nodes?.find(
      (node) => node.id === Features.compensations_management
    )?.available ?? false

  const legalEntities =
    data?.apiCore?.company?.legalEntities?.map((legalEntity) => ({
      label: legalEntity.legalName || '',
      value: Number(legalEntity.id),
    })) || []

  const firstLegalEntityId = legalEntities[0]?.value

  const form = useLazyRef(
    () =>
      new Form({
        legal_entity_id: firstLegalEntityId,
        status: 'to-do',
        effective_on: moment().format('YYYY-MM-DD'),
      })
  ).current

  const isValid =
    !!form.get('description').value &&
    !!form.get('subject').value &&
    !!form.get('employee_id').value

  const { loading, create: onSubmit } = useCreateManualIncidence(form, onSuccess || onClose)

  const onSubmitWrapper = () => {
    onSubmit()
    track(trackingEvents.payroll.createManualIncidenceSubmit, {
      subject: form.get('subject').value,
      description: form.get('description').value,
      employee: !!form.get('employee_id').value,
    })
    if (hasCompensationsModule) {
      track(trackingEvents.compensations.createManualEmployeeUpdate, {
        application: isBookkeeperView ? PROFESSIONALS_APP : FACTORIAL_APP,
      })
    }
  }

  if (queryLoading || !data) {
    return null
  }

  return (
    <Modal
      illustration="list-document"
      title={i18n.t('bookkeepers.incidence.manual.form.title')}
      description={
        hasCompensationsModule ? undefined : i18n.t('bookkeepers.incidence.manual.form.description')
      }
      onClose={onClose}
      onSubmit={onSubmitWrapper}
      disableFocusTrap
      footer={
        hasCompensationsModule ? (
          <>
            <Button onClick={onClose} type="secondary">
              {i18n.t('common.cancel')}
            </Button>
            <Button disabled={loading || !isValid} submit>
              {i18n.t('common.create')}
            </Button>
          </>
        ) : (
          <Button disabled={loading || !isValid} submit>
            {i18n.t('bookkeepers.incidence.manual.form.submit')}
          </Button>
        )
      }
    >
      <ManualIncidenceForm form={form} showLegalEntitySelector={!hasCompensationsModule} />
    </Modal>
  )
}

export default observer(New)
